<template>
  <div>
    <form-wrapper @submit="onSubmit">
      <div slot="box" v-loading="loading">
        <component-compiler
          v-for="(component, i) in getFields()"
          :content="component"
          :key="`form-${i}`"
        />
      </div>
      <form-nav
        :loading="loading"
        slot="navbar"
        @submit="onSubmit"
        :redirectRoute="redirectRoute"
        :hideLanguageSwitcher="true"
        :subtitle="`Status: ${form.status_display || ''}`"
        :titleAddition="studentFullName"
      >
        <div slot="middle" class="otp-code-controls">
          <el-button
            :disabled="loading || otpCodeRequestLoading || !otpCodeApplicantId"
            :loading="otpCodeRequestLoading"
            @click="generateOtpCode"
          >
            Generate verification code
          </el-button>
          <div class="otp-code-group">
            <el-input
              v-model="otpCode"
              type="text"
              :class="{
                'generate-button': true,
                'generate-button-expired': otpCodeIsExpired
              }"
              readonly
            />
            <span v-if="otpCodeExpiresIn" class="otp-code-expiry">
              {{ otpCodeExpiresIn }}
            </span>
          </div>
        </div>
        <component-compiler
          v-for="(component, i) in getNavButtons()"
          :content="component"
          :key="`form-${i}`"
        />
      </form-nav>
    </form-wrapper>
    <el-dialog
      :title="dialogTitle"
      append-to-body
      width="50vw"
      top="25vh"
      :visible.sync="dialogVisible"
    >
      <wysiwyg-input v-if="dialogVisible" v-model="comment" simple />
      <div v-show="commentError" class="comment-error">
        {{ commentError }}
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button @click="makeDecision()" type="primary">Submit</el-button>
      </span>
    </el-dialog>
    <el-dialog
      title="Refund amount"
      append-to-body
      width="400px"
      top="25vh"
      :visible.sync="refundDialogVisible"
    >
      <div class="refund-amount-label">{{ refundAmountLabel }}</div>
      <el-input-number
        v-model="refundAmount"
        :min="1"
        :precision="0"
        :step="1"
        controls-position="right"
      />
      <div v-show="refundAmountError" class="comment-error">
        {{ refundAmountError }}
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="refundDialogVisible = false">Cancel</el-button>
        <el-button
          @click="submitRefund()"
          :loading="refundRequestLoading"
          type="primary"
        >
          Submit
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import FormMixin from '@/crudBases/form/FormMixin'
import fields from '@/components/fields/'
import { CampApplication, User, CampCourse, CampLesson } from '@/api'
import { get } from 'lodash'
import moment from 'moment'

export default {
  mixins: [FormMixin],
  data() {
    return {
      form: {
        medical_reports: [],
        rosenberg_legacies: [],
        allergies: [],
        chronic_conditions: [],
        medications: [],
        mental_health_issues: [],
        student_passport_photo: [],
        legal_guardian_passport_photo: [],
        vaccination_record: [],
        other_document: []
      },
      redirectRoute: { name: 'Camp Applications List' },
      showLanguageSwitcher: false,
      showSubmitBtn: false,
      comment: '',
      commentError: '',
      dialogVisible: false,
      decisionService: null,
      refundDialogVisible: false,
      refundAmount: null,
      refundAmountError: '',
      refundRequestLoading: false,
      choices: {},
      otpCodeNow: Date.now(),
      otpCodeRequestLoading: false,
      otpCodeTimerId: null,
      otpCode: '',
      otpCodeExpiresAt: null
    }
  },
  mounted() {
    this.otpCodeTimerId = setInterval(() => {
      this.otpCodeNow = Date.now()
    }, 60000)
  },
  beforeDestroy() {
    clearInterval(this.otpCodeTimerId)
  },
  methods: {
    instanceFetchCallback() {
      this.prepareFormCopyToPreventChangesLost()
      const otpCodeData = get(this.form, 'otp_code_data')
      this.otpCode = get(otpCodeData, 'code', this.form.otp_code)
      this.otpCodeExpiresAt = get(otpCodeData, 'expires_at', null)
    },
    fetchDataCallback() {
      this.service.choices().then(response => {
        this.choices = response.data
      })
    },
    getFields() {
      const tabs = [
        this.getApplicantTab(),
        this.getStudentTab(),
        this.getParentsTab(),
        this.getCourseBookingTab(),
        this.getArrivalTab(),
        this.getRosenbergLegacyTab(),
        this.getMedicalInformationTab(),
        this.getDocumentsTab(),
        this.getBankingDataTab()
      ]
      if (this.form.decision_comment) {
        tabs.push(this.getDecisionCommentTab())
      }
      return [
        {
          component: 'el-tabs',
          children: tabs
        }
      ]
    },
    getApplicantTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Applicant information'
        },
        children: [
          new fields.Select({
            label: 'Relation',
            modelPath: [this, 'form', 'applicant_relation'],
            options: get(this.choices, 'applicant_relation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'student', 'applicant', 'first_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'student', 'applicant', 'last_name'],
            disabled: true
          }),
          new fields.Select({
            label: 'Salutation',
            modelPath: [this, 'form', 'applicant_salutation'],
            options: get(this.choices, 'salutation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'Salutation other',
            modelPath: [this, 'form', 'applicant_salutation_other'],
            condition: ['title', 'other'].includes(
              get(this.form, 'applicant_salutation')
            ),
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'applicant_occupation'],
            disabled: true
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'student', 'applicant', 'email'],
            disabled: true
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'applicant_secondary_email'],
            disabled: true,
            condition: !!this.form.applicant_secondary_email
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'applicant_phone'],
            disabled: true
          })
        ]
      }
    },
    getStudentTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Student information'
        },
        children: [
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'student', 'first_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Middle name',
            modelPath: [this, 'form', 'student', 'middle_name'],
            disabled: true,
            condition: get(this.form.student, 'middle_name')
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'student', 'last_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Birthdate',
            format: 'yyyy-MM-dd',
            modelPath: [this, 'form', 'student', 'birthdate'],
            disabled: true
          }),
          new fields.Input({
            label: 'Birthplace',
            modelPath: [this, 'form', 'student', 'birthplace'],
            disabled: true
          }),
          new fields.Select({
            label: 'Gender',
            modelPath: [this, 'form', 'student', 'gender'],
            options: get(this.choices, 'gender', []),
            disabled: true
          }),
          new fields.Select({
            label: 'Nationalities',
            modelPath: [this, 'form', 'student', 'nationalities'],
            disabled: true,
            multiple: true,
            options: []
          }),
          new fields.Input({
            label: 'Native language',
            modelPath: [this, 'form', 'student', 'native_language'],
            disabled: true
          }),
          new fields.Input({
            label: 'Address',
            modelPath: [this, 'form', 'student', 'address'],
            disabled: true
          }),
          new fields.Input({
            label: 'City',
            modelPath: [this, 'form', 'student', 'address_city'],
            disabled: true
          }),
          new fields.Input({
            label: 'Zipcode',
            modelPath: [this, 'form', 'student', 'address_zipcode'],
            disabled: true
          }),
          new fields.Input({
            label: 'Country',
            modelPath: [this, 'form', 'student', 'address_country'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Is billing address same',
            modelPath: [this, 'form', 'student', 'is_billing_address_same'],
            disabled: true
          }),
          new fields.Header({
            text: !get(this.form, 'student.is_billing_address_same')
              ? 'Billing address'
              : '',
            type: 'h4'
          }),
          new fields.Input({
            label: 'Billing address',
            modelPath: [this, 'form', 'student', 'billing_address'],
            disabled: true,
            condition: !get(this.form, 'student.is_billing_address_same')
          }),
          new fields.Input({
            label: 'City',
            modelPath: [this, 'form', 'student', 'billing_address_city'],
            disabled: true,
            condition: !get(this.form, 'student.is_billing_address_same')
          }),
          new fields.Input({
            label: 'Zipcode',
            modelPath: [this, 'form', 'student', 'billing_address_zipcode'],
            disabled: true,
            condition: !get(this.form, 'student.is_billing_address_same')
          }),
          new fields.Input({
            label: 'Country',
            modelPath: [this, 'form', 'student', 'billing_address_country'],
            disabled: true,
            condition: !get(this.form, 'student.is_billing_address_same')
          })
        ]
      }
    },
    getParentsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Parents & legal guardian'
        },
        children: [
          new fields.Header({
            text: 'Parent 1',
            type: 'h4'
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'parent_1_first_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'parent_1_last_name'],
            disabled: true
          }),
          new fields.Select({
            label: 'Salutation',
            modelPath: [this, 'form', 'parent_1_salutation'],
            options: get(this.choices, 'salutation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'Salutation other',
            modelPath: [this, 'form', 'parent_1_salutation_other'],
            condition: ['title', 'other'].includes(
              get(this.form, 'parent_1_salutation')
            ),
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'parent_1_occupation'],
            disabled: true
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'parent_1_email'],
            disabled: true
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'parent_1_secondary_email'],
            disabled: true,
            condition: !!this.form.parent_1_secondary_email
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'parent_1_phone'],
            disabled: true
          }),
          new fields.Header({
            text: 'Parent 2',
            type: 'h4',
            condition: !!this.form.parent_2_first_name
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'parent_2_first_name'],
            disabled: true,
            condition: !!this.form.parent_2_first_name
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'parent_2_last_name'],
            disabled: true,
            condition: !!this.form.parent_2_last_name
          }),
          new fields.Select({
            label: 'Salutation',
            modelPath: [this, 'form', 'parent_2_salutation'],
            options: get(this.choices, 'salutation', []),
            disabled: true,
            condition: !!this.form.parent_2_salutation
          }),
          new fields.Input({
            label: 'Salutation other',
            modelPath: [this, 'form', 'parent_2_salutation_other'],
            condition: ['title', 'other'].includes(
              get(this.form, 'parent_2_salutation')
            ),
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'parent_2_occupation'],
            disabled: true,
            condition: !!this.form.parent_2_occupation
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'parent_2_email'],
            disabled: true,
            condition: !!this.form.parent_2_email
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'parent_2_secondary_email'],
            disabled: true,
            condition: !!this.form.parent_2_secondary_email
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'parent_2_phone'],
            disabled: true,
            condition: !!this.form.parent_2_phone
          }),
          new fields.Header({
            text: 'Legal guardian',
            type: 'h4'
          }),
          new fields.Select({
            label: 'Relation',
            modelPath: [this, 'form', 'legal_guardian_relation'],
            options: get(this.choices, 'legal_guardian_relation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'legal_guardian_first_name'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'legal_guardian_last_name'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Select({
            label: 'Salutation',
            modelPath: [this, 'form', 'legal_guardian_salutation'],
            options: get(this.choices, 'salutation', []),
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Input({
            label: 'Salutation other',
            modelPath: [this, 'form', 'legal_guardian_salutation_other'],
            condition:
              this.showLegalGuardianFields &&
              ['title', 'other'].includes(
                get(this.form, 'legal_guardian_salutation')
              ),
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'legal_guardian_occupation'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'legal_guardian_email'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'legal_guardian_secondary_email'],
            condition:
              this.showLegalGuardianFields &&
              !!this.form.legal_guardian_secondary_email,
            disabled: true
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'legal_guardian_phone'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Header({
            text: 'Contact in legal guardian’s absence',
            type: 'h4'
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'contact_first_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'contact_last_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'contact_occupation'],
            disabled: true
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'contact_email'],
            disabled: true
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'contact_secondary_email'],
            disabled: true,
            condition: !!this.form.contact_secondary_email
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'contact_phone'],
            disabled: true
          }),
          new fields.Select({
            label: 'Child relation',
            modelPath: [this, 'form', 'contact_child_relation'],
            options: get(this.choices, 'parent_relation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'Child relation other',
            modelPath: [this, 'form', 'contact_child_relation_other'],
            condition: get(this.form, 'contact_child_relation') === 'other',
            disabled: true
          })
        ]
      }
    },
    getRosenbergLegacyTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Rosenberg legacy'
        },
        children: [
          new fields.Header({
            text: 'How did student know about Rosenberg',
            type: 'h4',
            condition: this.form.rosenberg_legacies.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'rosenberg_legacies'],
            disabled: true,
            noItemsText: '',
            items: this.form.rosenberg_legacies.map((item, i) => {
              return {
                headerLabel: get(item, ['legacy_type_display']),
                fields: [
                  new fields.Input({
                    label: 'First name',
                    modelPath: [
                      this,
                      'form',
                      'rosenberg_legacies',
                      i,
                      'first_name'
                    ],
                    condition: !['educational_consultant', 'other'].includes(
                      get(item, 'legacy_type')
                    ),
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Last name',
                    modelPath: [
                      this,
                      'form',
                      'rosenberg_legacies',
                      i,
                      'last_name'
                    ],
                    condition: !['educational_consultant', 'other'].includes(
                      get(item, 'legacy_type')
                    ),
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Other',
                    modelPath: [
                      this,
                      'form',
                      'rosenberg_legacies',
                      i,
                      'other_comment'
                    ],
                    condition: ['educational_consultant', 'other'].includes(
                      get(item, 'legacy_type')
                    ),
                    type: 'textarea',
                    disabled: true
                  })
                ]
              }
            })
          })
        ]
      }
    },
    getMedicalInformationTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Medical information'
        },
        children: [
          new fields.YesNo({
            label: 'Does the student have any allergies?',
            modelPath: [this, 'form', 'has_any_allergies'],
            disabled: true
          }),
          new fields.Header({
            text: 'Allergies',
            type: 'h4',
            condition: this.form.allergies.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'allergies'],
            disabled: true,
            noItemsText: '',
            blankObject: { reactions: [] },
            items: this.form.allergies.map((item, i) => {
              return {
                headerLabel: `Allergy ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Allergen type',
                    modelPath: [this, 'form', 'allergies', i, 'allergen_type'],
                    disabled: true
                  }),
                  new fields.Select({
                    label: 'Allergen subtypes',
                    multiple: true,
                    modelPath: [
                      this,
                      'form',
                      'allergies',
                      i,
                      'allergen_subtypes'
                    ],
                    options: [],
                    disabled: true
                  }),
                  new fields.Header({
                    text: 'Reactions',
                    type: 'h4',
                    condition: this.form.allergies[i].reactions.length
                  }),
                  this.getAllergyReactionsFormSet(
                    this.form.allergies[i].reactions,
                    i
                  )
                ]
              }
            })
          }),
          new fields.YesNo({
            label: 'Does the student have any chronic conditions?',
            modelPath: [this, 'form', 'has_any_chronic_conditions'],
            disabled: true
          }),
          new fields.Header({
            text: 'Chronic conditions',
            type: 'h4',
            condition: this.form.chronic_conditions.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'chronic_conditions'],
            disabled: true,
            noItemsText: '',
            items: this.form.chronic_conditions.map((item, i) => {
              return {
                headerLabel: `Chronic condition ${i + 1}`,
                fields: [
                  new fields.Select({
                    label: 'Chronic condition type',
                    modelPath: [
                      this,
                      'form',
                      'chronic_conditions',
                      i,
                      'condition_type'
                    ],
                    options: get(this.choices, 'condition_types', []),
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Other condition type',
                    modelPath: [
                      this,
                      'form',
                      'chronic_conditions',
                      i,
                      'other_condition_type'
                    ],
                    condition: get(item, 'condition_type') === 'other',
                    disabled: true
                  }),
                  new fields.Select({
                    label: 'Subtype',
                    multiple: true,
                    modelPath: [
                      this,
                      'form',
                      'chronic_conditions',
                      i,
                      'condition_subtype'
                    ],
                    options: [],
                    disabled: true
                  }),
                  new fields.Select({
                    label: 'Impact on daily routine',
                    multiple: true,
                    modelPath: [
                      this,
                      'form',
                      'chronic_conditions',
                      i,
                      'impact'
                    ],
                    options: get(this.choices, 'impacts_on_daily_routine', []),
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.YesNo({
            label: 'Does the student have any dietary restrictions?',
            modelPath: [this, 'form', 'has_any_dietary_restrictions'],
            disabled: true
          }),
          new fields.Input({
            label: 'Dietary restrictions',
            modelPath: [this, 'form', 'dietary_restrictions'],
            condition: this.form.has_any_dietary_restrictions,
            disabled: true
          }),
          new fields.YesNo({
            label: 'Does the student take any regular medication?',
            modelPath: [this, 'form', 'has_any_regular_medication'],
            disabled: true
          }),
          new fields.Header({
            text: 'Medications',
            type: 'h4',
            condition: this.form.medications.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'medications'],
            disabled: true,
            noItemsText: '',
            items: this.form.medications.map((item, i) => {
              return {
                headerLabel: `Medication ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Name of medication',
                    modelPath: [
                      this,
                      'form',
                      'medications',
                      i,
                      'medicine_name'
                    ],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Daily dose (mg)',
                    modelPath: [this, 'form', 'medications', i, 'dosage'],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Reason and diagnosis',
                    modelPath: [
                      this,
                      'form',
                      'medications',
                      i,
                      'reason_and_diagnosis'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Mental health issues',
            type: 'h4',
            condition: this.form.mental_health_issues.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'mental_health_issues'],
            disabled: true,
            noItemsText: '',
            items: this.form.mental_health_issues.map((item, i) => {
              return {
                headerLabel: `Mental health issue ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Name',
                    modelPath: [
                      this,
                      'form',
                      'mental_health_issues',
                      i,
                      'issue_name'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.YesNo({
            label: 'Does the student have any mental health issues?',
            modelPath: [this, 'form', 'has_any_mental_health_issues'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Has regular psychological care',
            modelPath: [this, 'form', 'has_regular_psychological_care'],
            disabled: true
          }),
          new fields.Header({
            text: 'Medical reports',
            type: 'h4',
            condition: this.form.medical_reports.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'medical_reports'],
            disabled: true,
            noItemsText: '',
            items: this.form.medical_reports.map((item, i) => {
              return {
                headerLabel: `Medical report ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    modelPath: [this, 'form', 'medical_reports', i, 'file']
                  })
                ]
              }
            })
          }),
          new fields.YesNo({
            label: 'Has tetanus vaccination?',
            modelPath: [this, 'form', 'has_tetanus_vaccination'],
            disabled: true
          }),
          new fields.Input({
            label: 'Tetanus vaccination date',
            modelPath: [this, 'form', 'tetanus_vaccination_date'],
            disabled: true,
            condition: this.form.has_tetanus_vaccination
          }),
          new fields.Select({
            label: "Student's fitness for sports",
            modelPath: [this, 'form', 'student_fitness_for_sports'],
            options: get(this.choices, 'fitness_for_sports', []),
            disabled: true
          }),
          new fields.Select({
            label: "Student's blood type",
            modelPath: [this, 'form', 'student_blood_type'],
            options: get(this.choices, 'blood_type', []),
            disabled: true
          }),
          new fields.YesNo({
            label: 'Is student swimmer',
            modelPath: [this, 'form', 'is_student_swimmer'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Is more medical information available',
            modelPath: [this, 'form', 'is_more_medical_information_available'],
            disabled: true
          }),
          new fields.Input({
            label: 'More medical information',
            modelPath: [this, 'form', 'more_medical_information'],
            condition: this.form.is_more_medical_information_available,
            disabled: true
          }),
          new fields.YesNo({
            label: 'Has student ever been in contact with drugs',
            modelPath: [this, 'form', 'has_student_contact_with_drugs'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Has student ever been expelled',
            modelPath: [this, 'form', 'has_been_student_expelled'],
            disabled: true
          })
        ]
      }
    },
    getDocumentsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Documents'
        },
        children: [
          new fields.AttachmentPreview({
            label: 'Student photo in passport format',
            modelPath: [
              this,
              'form',
              'student_photo_in_passport_format',
              'file'
            ]
          }),
          new fields.Header({
            text: 'Student passport photo',
            type: 'h4',
            condition: this.form.student_passport_photo.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'student_passport_photo'],
            disabled: true,
            noItemsText: '',
            items: this.form.student_passport_photo.map((item, i) => {
              return {
                headerLabel: `Student passport photo ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Student passport photo',
                    modelPath: [
                      this,
                      'form',
                      'student_passport_photo',
                      i,
                      'file'
                    ]
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Legal guardian passport photo',
            type: 'h4',
            condition: this.form.legal_guardian_passport_photo.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'legal_guardian_passport_photo'],
            disabled: true,
            noItemsText: '',
            items: this.form.legal_guardian_passport_photo.map((item, i) => {
              return {
                headerLabel: `Legal guardian passport photo ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Legal guardian passport photo',
                    modelPath: [
                      this,
                      'form',
                      'legal_guardian_passport_photo',
                      i,
                      'file'
                    ]
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Vaccination record',
            type: 'h4',
            condition: this.form.vaccination_record.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'vaccination_record'],
            disabled: true,
            noItemsText: '',
            items: this.form.vaccination_record.map((item, i) => {
              return {
                headerLabel: `Vaccination record ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Vaccination record',
                    modelPath: [this, 'form', 'vaccination_record', i, 'file']
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Other document',
            type: 'h4',
            condition: this.form.other_document.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'other_document'],
            disabled: true,
            noItemsText: '',
            items: this.form.other_document.map((item, i) => {
              return {
                headerLabel: `Other document ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Other document',
                    modelPath: [this, 'form', 'other_document', i, 'file']
                  })
                ]
              }
            })
          })
        ]
      }
    },
    getAllergyReactionsFormSet(reactions, allergy_index) {
      return new fields.Formset({
        modelPath: [this, 'form', 'allergies', allergy_index, 'reactions'],
        disabled: true,
        noItemsText: '',
        items: reactions.map((item, i) => {
          return {
            headerLabel: `Reaction ${i + 1}`,
            fields: [
              new fields.Input({
                label: 'Reaction name',
                modelPath: [
                  this,
                  'form',
                  'allergies',
                  allergy_index,
                  'reactions',
                  i,
                  'reaction'
                ],
                disabled: true
              }),
              new fields.Select({
                label: 'Reaction subtype (Severe)',
                multiple: true,
                modelPath: [
                  this,
                  'form',
                  'allergies',
                  allergy_index,
                  'reactions',
                  i,
                  'severe_reactions'
                ],
                options: [],
                disabled: true
              }),
              new fields.Select({
                label: 'Reaction subtype (Mild)',
                multiple: true,
                modelPath: [
                  this,
                  'form',
                  'allergies',
                  allergy_index,
                  'reactions',
                  i,
                  'mild_reactions'
                ],
                options: [],
                disabled: true
              })
            ]
          }
        })
      })
    },
    getCourseBookingTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Course booking'
        },
        children: [
          new fields.Input({
            label: 'Package',
            modelPath: [this, 'form', 'package'],
            disabled: true
          }),
          new fields.Input({
            label: 'Duration (weeks)',
            modelPath: [this, 'form', 'duration'],
            disabled: true
          }),
          new fields.Header({
            text: 'Focus courses',
            type: 'h4'
          }),
          new fields.Formset({
            label: 'Focus courses',
            modelPath: [this, 'form', 'focus_courses'],
            disabled: true,
            items: this.form.focus_courses.map((item, i) => {
              return {
                headerLabel: `Focus course ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Course',
                    modelPath: [this, 'form', 'focus_courses', i, 'course'],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Amount',
                    modelPath: [this, 'form', 'focus_courses', i, 'amount'],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Level',
                    condition: !!get(item, 'level'),
                    modelPath: [
                      this,
                      'form',
                      'focus_courses',
                      i,
                      'level_display'
                    ]
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Private lessons',
            type: 'h4'
          }),
          new fields.Formset({
            label: 'Private lessons',
            modelPath: [this, 'form', 'private_lessons'],
            disabled: true,
            items: this.form.private_lessons.map((item, i) => {
              return {
                headerLabel: `Private lesson ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Lesson',
                    modelPath: [this, 'form', 'private_lessons', i, 'lesson'],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Amount',
                    modelPath: [this, 'form', 'private_lessons', i, 'amount'],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Level',
                    condition: !!get(item, 'level'),
                    modelPath: [
                      this,
                      'form',
                      'private_lessons',
                      i,
                      'level_display'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.Input({
            label: 'Additional comments',
            modelPath: [this, 'form', 'additional_comments'],
            disabled: true
          }),
          new fields.Input({
            label: 'Price',
            modelPath: [this, 'form', 'price'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Terms',
            modelPath: [this, 'form', 'term'],
            disabled: true
          }),
          new fields.Invoice({
            label: 'Invoice',
            modelPath: [this, 'form', 'invoice'],
            disabled: true
          })
        ]
      }
    },
    getArrivalTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Arrival'
        },
        children: [
          new fields.Input({
            label: 'Arrival',
            modelPath: [this, 'form', 'arrival'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Airport collection',
            modelPath: [this, 'form', 'airport_service'],
            disabled: true
          }),
          new fields.Input({
            label: 'Origin',
            modelPath: [this, 'form', 'airport_origin'],
            condition: this.form.airport_service,
            disabled: true
          }),
          new fields.Select({
            label: 'Contact person',
            modelPath: [this, 'form', 'airport_contact_person'],
            condition: this.form.airport_service,
            disabled: true,
            options: [
              { label: 'First parent', value: 'first_parent' },
              { label: 'Second parent', value: 'second_parent' },
              { label: 'Other', value: 'other' }
            ]
          }),
          new fields.Input({
            label: 'Contact person first name',
            modelPath: [this, 'form', 'airport_contact_first_name'],
            condition:
              this.form.airport_service &&
              this.form.airport_contact_person == 'other',
            disabled: true
          }),
          new fields.Input({
            label: 'Contact person last name',
            modelPath: [this, 'form', 'airport_contact_last_name'],
            condition:
              this.form.airport_service &&
              this.form.airport_contact_person == 'other',
            disabled: true
          }),
          new fields.Select({
            label: 'Contact person salutation',
            modelPath: [this, 'form', 'airport_contact_salutation'],
            options: get(this.choices, 'salutation', []),
            disabled: true,
            condition:
              this.form.airport_service &&
              this.form.airport_contact_person == 'other'
          }),
          new fields.Input({
            label: 'Contact person salutation other',
            modelPath: [this, 'form', 'airport_contact_salutation_other'],
            condition:
              this.form.airport_service &&
              this.form.airport_contact_person == 'other' &&
              ['title', 'other'].includes(
                get(this.form, 'airport_contact_salutation')
              ),
            disabled: true
          }),
          new fields.Input({
            label: 'Contact person occupation',
            modelPath: [this, 'form', 'airport_contact_occupation'],
            condition:
              this.form.airport_service &&
              this.form.airport_contact_person == 'other',
            disabled: true
          }),
          new fields.Input({
            label: 'Contact person email',
            modelPath: [this, 'form', 'airport_contact_email'],
            condition:
              this.form.airport_service &&
              this.form.airport_contact_person == 'other',
            disabled: true
          }),
          new fields.Input({
            label: 'Contact person secondary email',
            modelPath: [this, 'form', 'airport_contact_secondary_email'],
            condition:
              this.form.airport_service &&
              this.form.airport_contact_person == 'other',
            disabled: true
          }),
          new fields.Input({
            label: 'Contact person phone',
            modelPath: [this, 'form', 'airport_contact_phone'],
            condition:
              this.form.airport_service &&
              this.form.airport_contact_person == 'other',
            disabled: true
          }),
          new fields.Input({
            label: 'Flight number',
            modelPath: [this, 'form', 'flight_number'],
            condition: this.form.airport_service,
            disabled: true
          }),
          new fields.Input({
            label: 'Train number',
            modelPath: [this, 'form', 'train_number'],
            condition: this.form.airport_service,
            disabled: true
          }),
          new fields.YesNo({
            label: 'Driver',
            modelPath: [this, 'form', 'is_driver_needed'],
            condition: this.form.airport_service,
            disabled: true
          }),
          new fields.Input({
            label: 'Requested arrival',
            modelPath: [this, 'form', 'requested_arrival'],
            condition: this.form.airport_service,
            disabled: true
          })
        ]
      }
    },
    getBankingDataTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Banking data'
        },
        children: [
          new fields.Input({
            label: 'First name of account holder',
            modelPath: [this, 'form', 'refund_first_name_of_account_holder'],
            disabled: true
          }),
          new fields.Input({
            label: 'Last name of account holder',
            modelPath: [this, 'form', 'refund_last_name_of_account_holder'],
            disabled: true
          }),
          new fields.Input({
            label: 'Address of account holder',
            type: 'textarea',
            modelPath: [this, 'form', 'refund_address_of_account_holder'],
            disabled: true
          }),
          new fields.Input({
            label: 'Zip code',
            modelPath: [this, 'form', 'refund_postal_code_of_account_holder'],
            disabled: true
          }),
          new fields.Input({
            label: 'City',
            modelPath: [this, 'form', 'refund_city_of_account_holder'],
            disabled: true
          }),
          new fields.Input({
            label: 'Country',
            modelPath: [this, 'form', 'refund_country_of_account_holder'],
            disabled: true
          }),
          new fields.Input({
            label: 'Name of bank',
            modelPath: [this, 'form', 'refund_name_of_bank'],
            disabled: true
          }),
          new fields.Input({
            label: 'Zip code',
            modelPath: [this, 'form', 'refund_postal_code_of_bank'],
            disabled: true
          }),
          new fields.Input({
            label: 'City',
            modelPath: [this, 'form', 'refund_city_of_bank'],
            disabled: true
          }),
          new fields.Input({
            label: 'Country',
            modelPath: [this, 'form', 'refund_country_of_bank'],
            disabled: true
          }),
          new fields.Input({
            label: 'IBAN/Account Number',
            modelPath: [this, 'form', 'refund_account_number'],
            disabled: true
          }),
          new fields.Input({
            label: 'Swift/BIC',
            modelPath: [this, 'form', 'refund_swift_bic'],
            disabled: true
          }),
          new fields.Input({
            label: 'Clearing number',
            modelPath: [this, 'form', 'refund_clearing_number'],
            disabled: true
          })
        ]
      }
    },
    getDecisionCommentTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Decision message'
        },
        children: [
          new fields.WYSIWYG({
            label: 'Message',
            modelPath: [this, 'form', 'decision_comment'],
            readonly: true,
            disabled: true
          })
        ]
      }
    },
    markAsPaid() {
      this.service
        .markAsPaid(this.lookupParams)
        .then(() => {
          this.$message({
            message: 'Status has been changed',
            type: 'success'
          })
          this.fetchData()
        })
        .catch(this.errorHandler)
    },
    markProcessing() {
      this.service
        .markProcessing(this.lookupParams)
        .then(() => {
          this.$message({
            message: 'Status has been changed',
            type: 'success'
          })
          this.fetchData()
        })
        .catch(this.errorHandler)
    },
    markApproved() {
      this.decisionService = this.service.markApproved
      this.dialogVisible = true
    },
    markUnsuccessful() {
      this.decisionService = this.service.markUnsuccessful
      this.dialogVisible = true
    },
    refund() {
      this.refundAmount = Number(this.form.price) + Number(this.form.deposit)
      this.refundAmountError = ''
      this.refundDialogVisible = true
    },
    submitRefund() {
      this.refundRequestLoading = true
      this.service
        .refund(this.lookupParams, { amount: this.refundAmount })
        .then(() => {
          this.$message({
            message: 'Application has been refunded',
            type: 'success'
          })
          this.fetchData()
          this.refundDialogVisible = false
        })
        .catch(error => {
          const responseData = get(error, 'response.data', {})
          const errorMessage = this.getRefundErrorMessage(responseData)
          this.refundAmountError = get(responseData, 'amount.0', '')

          if (get(error, 'response.status') === 400 && errorMessage) {
            this.$message({
              message: errorMessage,
              type: 'error'
            })
            if (!this.refundAmountError) {
              this.refundAmountError = errorMessage
            }
            return
          }

          this.errorHandler(error)
        })
        .finally(() => {
          this.refundRequestLoading = false
        })
    },
    getRefundErrorMessage(responseData) {
      if (!responseData) {
        return ''
      }

      if (typeof responseData === 'string') {
        return responseData
      }

      const message = get(responseData, 'detail')
      if (message) {
        return message
      }

      const nonFieldErrors = get(responseData, 'non_field_errors')
      if (nonFieldErrors) {
        return Array.isArray(nonFieldErrors)
          ? nonFieldErrors.join(', ')
          : nonFieldErrors
      }

      const amountErrors = get(responseData, 'amount')
      if (amountErrors) {
        return Array.isArray(amountErrors)
          ? amountErrors.join(', ')
          : amountErrors
      }

      return Object.values(responseData)
        .reduce((messages, value) => {
          return messages.concat(Array.isArray(value) ? value : [value])
        }, [])
        .filter(Boolean)
        .join(', ')
    },
    generateOtpCode() {
      if (!this.otpCodeApplicantId || this.otpCodeRequestLoading) {
        return
      }

      this.otpCodeRequestLoading = true
      User.createOTPCode({ id: this.otpCodeApplicantId })
        .then(response => {
          this.otpCode = response.data.code
          this.otpCodeExpiresAt = response.data.expires_at
        })
        .catch(error => {
          this.errorHandler(error)
        })
        .finally(() => {
          this.otpCodeRequestLoading = false
        })
    },
    makeDecision() {
      this.decisionService(this.lookupParams, {
        decision_comment: this.comment
      })
        .then(() => {
          this.$message({
            message: 'Status has been changed',
            type: 'success'
          })
          this.fetchData()
          this.commentError = ''
          this.dialogVisible = false
        })
        .catch(error => {
          if (error.response.data.non_field_errors) {
            this.$message({
              message: error.response.data.non_field_errors,
              type: 'error'
            })
          }
          this.commentError = error.response.data.decision_comment[0]
        })
    },
    getNavButtons() {
      return [
        {
          component: 'router-link',
          attrs: {
            to: { ...this.redirectRoute, query: this.$route.query }
          },
          condition: this.redirectRoute,
          children: [
            {
              component: 'el-button',
              innerText: 'Cancel'
            }
          ]
        },
        {
          component: 'el-button',
          attrs: {
            type: 'warning',
            disabled: this.form.status !== 'completed'
          },
          listeners: {
            click: this.markAsPaid
          },
          innerText: 'Mark as paid'
        },
        {
          component: 'el-button',
          attrs: {
            type: 'primary',
            disabled: this.form.status !== 'paid'
          },
          listeners: {
            click: this.markProcessing
          },
          innerText: 'Start processing'
        },
        {
          component: 'el-button',
          attrs: {
            disabled: !this.isRefundAvailable
          },
          listeners: {
            click: this.refund
          },
          innerText: 'Refund'
        },
        {
          component: 'el-button',
          attrs: {
            type: 'success',
            disabled: this.form.status !== 'processing'
          },
          listeners: {
            click: this.markApproved
          },
          innerText: 'Approve'
        },
        {
          component: 'el-button',
          attrs: {
            type: 'danger',
            disabled: this.form.status !== 'processing'
          },
          listeners: {
            click: this.markUnsuccessful
          },
          innerText: 'Reject'
        }
      ]
    }
  },
  computed: {
    service() {
      return CampApplication
    },
    otpCodeApplicantId() {
      return get(this.form, 'student.applicant.id', null)
    },
    otpCodeIsExpired() {
      if (!this.otpCodeExpiresAt) {
        return false
      }
      return moment(this.otpCodeExpiresAt).isBefore(moment(this.otpCodeNow))
    },
    otpCodeExpiresIn() {
      if (!this.otpCodeExpiresAt) {
        return ''
      }
      const expiryMoment = moment(this.otpCodeExpiresAt)
      if (this.otpCodeIsExpired) {
        return `expired ${expiryMoment.fromNow()}`
      }
      return `expires ${expiryMoment.fromNow()}`
    },
    postalAddress() {
      return `${this.form.street}, ${this.form.postal_code} ${this.form.city}, ${this.form.country}`
    },
    studentFullName() {
      const firstName = get(this.form, 'student.first_name', '')
      const lastName = get(this.form, 'student.last_name', '')
      return `- ${firstName} ${lastName}`
    },
    dialogTitle() {
      return this.decisionService === this.service.markApproved
        ? 'Approval message'
        : 'Rejection message'
    },
    refundAmountLabel() {
      return this.form.currency
        ? `Refund amount, ${this.form.currency}`
        : 'Refund amount'
    },
    isRefundAvailable() {
      return ['paid', 'approved', 'unsuccessful'].includes(this.form.status)
    },
    showLegalGuardianFields() {
      return !['first_parent', 'second_parent', 'both_parents'].includes(
        this.form.legal_guardian_relation
      )
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep .is-disabled [class$='inner'],
::v-deep .el-select__tags-text {
  color: #303133 !important;
}

::v-deep .el-select {
  &__caret {
    display: none;
  }
}
::v-deep .el-form-item__label {
  width: 100% !important;
  margin-bottom: 0.5rem;
  text-align: left;
}
::v-deep .el-form-item__content {
  margin-left: 0 !important;
}
.comment-error {
  color: #f56c6c;
  font-size: 12px;
  line-height: 1;
  padding-top: 4px;
  top: 100%;
  left: 0;
}
::v-deep .formset-wrapper .formset-wrapper {
  margin-left: 20px;
}
.otp-code-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.otp-code-group {
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
}

.generate-button {
  width: 80px;
}

.generate-button-expired::v-deep .el-input__inner {
  color: #909399;
  text-decoration: line-through;
}

.otp-code-expiry {
  margin-left: 8px;
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
}

.refund-amount-label {
  margin-bottom: 8px;
}
</style>
